import { useEffect, useRef } from 'react';
import { compose, either, equals, findIndex, isEmpty, isNil, lte, nth, propSatisfies } from 'ramda';
import { ptiaVersementEnum } from '@@viewEnums';
import { useWatch } from 'react-hook-form';

const useExtraRules = ({ form }) => {
  let { current } = useRef(true);

  const ptiaVersementTTCause = useWatch({
    control: form.control,
    name: 'guarantees.TT_CAUSE_DEP.value'
  });

  const ptiaVersementAcc = useWatch({
    control: form.control,
    name: 'guarantees.ACC_DEP.value'
  });

  const disabledPtiaVersementRenteTTCause = equals(ptiaVersementTTCause, ptiaVersementEnum.RENTE);
  const disabledPtiaVersementRenteAcc = equals(ptiaVersementAcc, ptiaVersementEnum.RENTE);
  const disabledPtiaVersementCapitalAcc = equals(ptiaVersementAcc, ptiaVersementEnum.CAPITAL);

  useEffect(() => {
    if (current) {
      if (disabledPtiaVersementRenteTTCause) {
        form.setValue('guarantees.OPTION_TT_CAUSE_DOUBL_ACC.selected', false);
        form.setValue('guarantees.OPTION_TT_CAUSE_DOUBL_EFFET.selected', false);
      }

      if (disabledPtiaVersementRenteAcc) {
        form.setValue('guarantees.OPTION_ACC_DOUBL_ACC.selected', false);
        form.setValue('guarantees.OPTION_ACC_DOUBL_EFFET.selected', false);
      }

      if (disabledPtiaVersementCapitalAcc) {
        form.setValue('guarantees.OPTION_ACC_DOUBL_ACC.selected', false);
      }

      current = false;
    }
  }, [disabledPtiaVersementRenteTTCause, disabledPtiaVersementRenteAcc, form]);

  const optionTtCause = form.watch('guarantees.OPTION_TT_CAUSE.selected');
  const optionAcc = form.watch('guarantees.OPTION_ACC.selected');
  const optionIncap = form.watch('guarantees.OPTION_INCAP.selected');
  const optionInval = form.watch('guarantees.OPTION_INVAL.selected');

  useEffect(() => {
    if (current) {
      if (!optionTtCause && !optionAcc) {
        form.setValue('guarantees.OPTION_INCAP.selected', true);
        form.setValue('guarantees.OPTION_INVAL.selected', true);
      }
    }
  }, [optionTtCause, optionAcc]);

  useEffect(() => {
    if (optionTtCause) {
      form.setValue('guarantees.TT_CAUSE_DEP.value', ptiaVersementTTCause || ptiaVersementEnum.RENTE);
    }

    if (optionAcc) {
      form.setValue('guarantees.ACC_DEP.value', ptiaVersementAcc || ptiaVersementEnum.RENTE);
    }
  }, [optionTtCause, optionAcc, ptiaVersementTTCause, ptiaVersementAcc]);

  useEffect(() => {
    if (!optionTtCause && !optionAcc) {
      if (!optionInval) {
        form.setValue('guarantees.OPTION_INCAP.selected', true);
      }
    }
  }, [optionTtCause, optionAcc, optionInval]);

  useEffect(() => {
    if (!optionTtCause && !optionAcc) {
      form.setValue('guarantees.OPTION_REDUC.editable', false);
      form.setValue('guarantees.OPTION_REDUC.selected', false);
      form.setValue('guarantees.OPTION_RCONJ.editable', false);
      form.setValue('guarantees.OPTION_RCONJ.selected', false);
    }

    if (optionTtCause || optionAcc) {
      form.setValue('guarantees.OPTION_REDUC.editable', true);
      form.setValue('guarantees.OPTION_RCONJ.editable', true);
    }
  }, [optionTtCause, optionAcc]);

  useEffect(() => {
    if (!optionTtCause && !optionAcc) {
      if (!optionIncap) {
        form.setValue('guarantees.OPTION_INVAL.selected', true);
      }
    }
  }, [optionTtCause, optionAcc, optionIncap]);

  const isNilOrEmpty = compose(either(isNil, isEmpty));

  const onOptionFrMalChange = (value) => {
    const currentAcc = form.getValues('guarantees.OPTION_FR_ACC.value');

    if (currentAcc > value) {
      form.setValue('guarantees.OPTION_FR_ACC.value', value);
      form.setValue('guarantees.OPTION_FR_HOSP.value', value);
    }

    const accOptions = form.getValues('guarantees.OPTION_FR_ACC.options');
    if (!isNilOrEmpty(accOptions)) {
      accOptions.forEach((option, index) => {
        form.setValue(
          `guarantees.OPTION_FR_ACC.options.${index}.editable`,
          parseInt(option.value) <= value
        );
      });
    }

    const hospOptions = form.getValues('guarantees.OPTION_FR_HOSP.options');
    if (!isNilOrEmpty(hospOptions)) {
      hospOptions.forEach((option, index) => {
        form.setValue(
          `guarantees.OPTION_FR_HOSP.options.${index}.editable`,
          parseInt(option.value) <= value
        );
      });
    }

    if (value < 30) {
      form.setValue('guarantees.OPTION_ZERO_SIN.selected', false);
      form.setValue('guarantees.OPTION_ZERO_SIN.editable', false);
    } else {
      form.setValue('guarantees.OPTION_ZERO_SIN.editable', true);
    }
  };

  const updateMalOptionsEditability = (value) => {
    const malOptions = form.getValues('guarantees.OPTION_FR_MAL.options');
    if (!isNilOrEmpty(malOptions)) {
      malOptions.forEach((option, index) => {
        form.setValue(
          `guarantees.OPTION_FR_MAL.options.${index}.editable`,
          parseInt(option.value) >= value
        );
      });
    }
  };

  const onOptionFrAccChange = (value) => {
    form.setValue('guarantees.OPTION_FR_HOSP.value', value);
    updateMalOptionsEditability(value);
  };

  const onOptionFrHospChange = (value) => {
    form.setValue('guarantees.OPTION_FR_ACC.value', value);
    updateMalOptionsEditability(value);
  };

  const watchedOptionIncapCap = form.watch('guarantees.OPTION_INCAP_CAP.value');
  const watchedFrGenCap = form.watch('guarantees.FR_GEN_CAP.value');
  const optionIncapCapOptions = form.watch('guarantees.OPTION_INCAP_CAP.options');

  useEffect(() => {
    if (!isNilOrEmpty(optionIncapCapOptions)) {
      const watchedOptionIncapCapInt = parseInt(watchedOptionIncapCap);
      const watchedFrGenCapInt = parseInt(watchedFrGenCap);

      optionIncapCapOptions.forEach((option, index) => {
        const optionValue = parseInt(option.value);

        form.setValue(
          `guarantees.OPTION_INCAP_CAP.options.${index}.editable`,
          optionValue >= watchedFrGenCapInt
        );
      });
      // Si la valeur OPTION_INCAP_CAP déjà sélectionnée est inférieure à la valeur FR_GEN_CAP sélectionnée
      // Alors => On récupère l'option OPTION_INCAP_CAP dont la valeur est la moins élevée mais supérieure à la valeur FR_GEN_CAP sélectionnée
      // Sinon => On ne fait rien
      if (watchedOptionIncapCapInt < watchedFrGenCapInt) {
        const higherOptionIncapCapOptionIndex = findIndex(
          propSatisfies(lte(watchedFrGenCapInt), 'value'),
          optionIncapCapOptions
        );

        if (higherOptionIncapCapOptionIndex > -1) {
          const higherOptionIncapCapOption = nth(
            higherOptionIncapCapOptionIndex,
            optionIncapCapOptions
          );

          form.setValue(
            `guarantees.OPTION_INCAP_CAP.options.${higherOptionIncapCapOptionIndex}.selected`,
            true
          );
          form.setValue('guarantees.OPTION_INCAP_CAP.value', higherOptionIncapCapOption.value);
        }
      }
    }
  }, [watchedFrGenCap]);

  return {
    disabledPtiaVersementRenteTTCause,
    disabledPtiaVersementRenteAcc,
    disabledPtiaVersementCapitalAcc,
    onOptionFrMalChange,
    onOptionFrAccChange,
    onOptionFrHospChange
  };
};

export default useExtraRules;
